import { API_BASE_URL, getAuthToken, handleApiError } from './apiUtils';

export interface JobApplicant {
    id: number;
    name: string;
    email: string;
    phone: string;
    designation: string;
    experience: string;
    education: string;
    resume_url?: string;
    status: 'pending' | 'shortlisted' | 'rejected' | 'hired';
    created_at: string;
    updated_at: string;
}

export interface CreateJobApplicantData {
    name: string;
    email: string;
    phone: string;
    designation: string;
    experience: string;
    education: string;
    resume?: File;
}

export interface UpdateJobApplicantData {
    name?: string;
    email?: string;
    phone?: string;
    designation?: string;
    experience?: string;
    education?: string;
    status?: string;
    resume?: File;
}

export interface JobApplicantsListParams {
    designation?: string;
    status?: string;
    query?: string;
    page?: number;
    per_page?: number;
}

export interface JobApplicantsListResponse {
    applicants: JobApplicant[];
    pagination: {
        current_page: number;
        last_page: number;
        per_page: number;
        total: number;
        from: number;
        to: number;
    };
}

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

export interface BackendResponse<T> {
    data: T;
    message?: string;
}

export const jobApplicantApi = {
    getJobApplicants: async (params: JobApplicantsListParams = {}): Promise<JobApplicantsListResponse> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');
        
        const queryParams = new URLSearchParams();
        Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                queryParams.append(key, value.toString());
            }
        });
        
        const response = await fetch(`${API_BASE_URL}/job-applicants?${queryParams}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        
        if (!response.ok) {
            await handleApiError(response);
        }
        
        const data: BackendResponse<JobApplicantsListResponse> = await response.json();
        return data.data;
    },

    getJobApplicant: async (id: number): Promise<JobApplicant> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');
        
        const response = await fetch(`${API_BASE_URL}/job-applicants/${id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        
        if (!response.ok) {
            await handleApiError(response);
        }
        
        const data: BackendResponse<JobApplicant> = await response.json();
        return data.data;
    },

    createJobApplicant: async (applicantData: CreateJobApplicantData): Promise<JobApplicant> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');
        
        const formData = new FormData();
        Object.entries(applicantData).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value);
            }
        });
        
        const response = await fetch(`${API_BASE_URL}/job-applicants`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });
        
        if (!response.ok) {
            await handleApiError(response);
        }
        
        const data: BackendResponse<JobApplicant> = await response.json();
        return data.data;
    },

    updateJobApplicant: async (id: number, applicantData: UpdateJobApplicantData): Promise<JobApplicant> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');
        
        const formData = new FormData();
        Object.entries(applicantData).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
                formData.append(key, value);
            }
        });
        
        const response = await fetch(`${API_BASE_URL}/job-applicants/${id}`, {
            method: 'PUT',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            body: formData,
        });
        
        if (!response.ok) {
            await handleApiError(response);
        }
        
        const data: BackendResponse<JobApplicant> = await response.json();
        return data.data;
    },

    deleteJobApplicant: async (id: number): Promise<void> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');
        
        const response = await fetch(`${API_BASE_URL}/job-applicants/${id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        
        if (!response.ok) {
            await handleApiError(response);
        }
    },

    updateJobApplicantStatus: async (id: number, status: string): Promise<JobApplicant> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');
        
        const response = await fetch(`${API_BASE_URL}/job-applicants/${id}/status`, {
            method: 'PATCH',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ status }),
        });
        
        if (!response.ok) {
            await handleApiError(response);
        }
        
        const data: BackendResponse<JobApplicant> = await response.json();
        return data.data;
    },

    getDesignations: async (): Promise<string[]> => {
        const token = getAuthToken();
        if (!token) throw new Error('No authentication token');
        
        const response = await fetch(`${API_BASE_URL}/job-applicants/designations`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
        
        if (!response.ok) {
            await handleApiError(response);
        }
        
        const data: BackendResponse<string[]> = await response.json();
        return data.data;
    },
};
